/**
 * Data Processing Utilities for Analytics
 * Handles event validation, compression, and batching
 */

/**
 * Validate event structure
 */
export const validateEvent = (event) => {
  if (!event || typeof event !== 'object') return false;
  if (!event.eventName || typeof event.eventName !== 'string') return false;
  if (!event.properties || typeof event.properties !== 'object') return false;
  return true;
};

/**
 * Sanitize event data to remove sensitive information
 */
export const sanitizeEventData = (data) => {
  const sanitized = { ...data };
  
  // Remove sensitive fields
  const sensitiveFields = ['password', 'token', 'apiKey', 'secret'];
  sensitiveFields.forEach(field => {
    delete sanitized[field];
  });
  
  // Truncate long strings
  Object.keys(sanitized).forEach(key => {
    if (typeof sanitized[key] === 'string' && sanitized[key].length > 1000) {
      sanitized[key] = sanitized[key].substring(0, 1000) + '...';
    }
  });
  
  return sanitized;
};

/**
 * Compress events for storage (simple compression)
 */
export const compressEvents = (events) => {
  if (!Array.isArray(events) || events.length === 0) return events;
  
  return events.map(event => ({
    e: event.eventName,
    p: {
      uid: event.properties.userId,
      sid: event.properties.sessionId,
      ts: event.properties.timestamp,
      url: event.properties.url,
      path: event.properties.path,
      ...Object.keys(event.properties)
        .filter(key => !['userId', 'sessionId', 'timestamp', 'url', 'path'].includes(key))
        .reduce((acc, key) => {
          acc[key] = event.properties[key];
          return acc;
        }, {})
    }
  }));
};

/**
 * Decompress events from storage
 */
export const decompressEvents = (compressedEvents) => {
  if (!Array.isArray(compressedEvents) || compressedEvents.length === 0) return compressedEvents;
  
  if (!compressedEvents[0].e) return compressedEvents;
  
  return compressedEvents.map(compressed => ({
    eventName: compressed.e,
    properties: {
      userId: compressed.p.uid,
      sessionId: compressed.p.sid,
      timestamp: compressed.p.ts,
      url: compressed.p.url,
      path: compressed.p.path,
      ...Object.keys(compressed.p)
        .filter(key => !['uid', 'sid', 'ts', 'url', 'path'].includes(key))
        .reduce((acc, key) => {
          acc[key] = compressed.p[key];
          return acc;
        }, {})
    }
  }));
};

/**
 * Create event batches from array
 */
export const createEventBatches = (events, batchSize = 20) => {
  const batches = [];
  for (let i = 0; i < events.length; i += batchSize) {
    batches.push(events.slice(i, i + batchSize));
  }
  return batches;
};

/**
 * Validate event data types
 */
export const validateEventData = (data) => {
  const errors = [];
  
  if (!data.eventName) errors.push('eventName is required');
  if (!data.properties) errors.push('properties is required');
  if (data.properties && !data.properties.timestamp) errors.push('timestamp is required');
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Format event for backend
 */
export const formatEventForBackend = (event) => {
  return {
    eventName: event.eventName,
    properties: sanitizeEventData(event.properties),
    timestamp: new Date().toISOString()
  };
};